import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LiquidGlassCard } from './components/glass/LiquidGlassCard';
import { LiquidButton } from './components/glass/LiquidButton';
import ShapeHero from './components/backgrounds/ShapeHero';
import ShimmerText from './components/text/ShimmerText';
import { LogIn, User, Hash } from 'lucide-react';

interface SenderLobbyProps {
    onJoin: (name: string, roomId: string) => void;
    config: any;
    initialRoomId?: string;
}

export const SenderLobby: React.FC<SenderLobbyProps> = ({ onJoin, config, initialRoomId }) => {
    const [name, setName] = useState('');
    const [roomId, setRoomId] = useState(initialRoomId || '');
    const [errors, setErrors] = useState<{ name?: string; roomId?: string }>({});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        const newErrors: { name?: string; roomId?: string } = {};
        
        if (!name.trim()) {
            newErrors.name = 'Name is required';
        }
        
        if (!roomId.trim()) {
            newErrors.roomId = 'Room ID is required';
        } else if (!/^\d{7}$/.test(roomId.trim())) {
            newErrors.roomId = 'Room ID must be 7 digits';
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        
        setErrors({});
        onJoin(name.trim(), roomId.trim());
    };

    return (
        <ShapeHero>
            <div className="flex flex-col items-center justify-center min-h-screen py-8 px-4">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-8"
                >
                    <img src="./EllingtonLogo.svg" className="w-48 mx-auto mb-6" alt="logo" />
                    <ShimmerText
                        text="Join as Sender"
                        className="text-3xl md:text-5xl"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="w-full max-w-md"
                >
                    <LiquidGlassCard className="border-white/10 bg-white/5 backdrop-blur-xl">
                        <form onSubmit={handleSubmit} className="space-y-6 p-6">
                            <div className="text-center space-y-2">
                                <h3 className="text-2xl font-semibold text-white">Sender Mode</h3>
                                <p className="text-white/60 text-sm">
                                    Enter your details to join the room
                                </p>
                            </div>

                            <div className="h-px bg-white/10" />

                            {/* Name Input */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
                                    <User className="w-4 h-4" />
                                    Your Name
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        if (errors.name) setErrors({ ...errors, name: undefined });
                                    }}
                                    placeholder="Enter your name"
                                    className={`w-full px-4 py-3 bg-white/5 border ${
                                        errors.name ? 'border-red-500/50' : 'border-white/10'
                                    } rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all`}
                                />
                                {errors.name && (
                                    <p className="text-red-400 text-sm">{errors.name}</p>
                                )}
                            </div>

                            {/* Room ID Input */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-white/80 text-sm font-medium">
                                    <Hash className="w-4 h-4" />
                                    Room ID
                                </label>
                                <input
                                    type="text"
                                    value={roomId}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, '').slice(0, 7);
                                        setRoomId(value);
                                        if (errors.roomId) setErrors({ ...errors, roomId: undefined });
                                    }}
                                    placeholder="7-digit Room ID"
                                    maxLength={7}
                                    className={`w-full px-4 py-3 bg-white/5 border ${
                                        errors.roomId ? 'border-red-500/50' : 'border-white/10'
                                    } rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all text-center text-2xl tracking-wider font-semibold`}
                                />
                                {errors.roomId && (
                                    <p className="text-red-400 text-sm">{errors.roomId}</p>
                                )}
                                <p className="text-white/40 text-xs text-center">
                                    {roomId.length}/7 digits
                                </p>
                            </div>

                            <LiquidButton
                                type="submit"
                                className="w-full bg-gradient-to-b from-blue-600/80 to-blue-700/80 hover:from-blue-500/80 hover:to-blue-600/80 text-white border-0 py-6 text-lg font-semibold shadow-2xl"
                            >
                                <LogIn className="w-5 h-5 mr-2" />
                                Join Room
                            </LiquidButton>
                        </form>
                    </LiquidGlassCard>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-8 text-white/40 text-sm text-center"
                >
                    Ellington {config.version} |{' '}
                    <a
                        href="https://github.com/inshare/server/"
                        className="text-blue-400 hover:text-blue-300 underline"
                    >
                        GitHub
                    </a>
                </motion.div>
            </div>
        </ShapeHero>
    );
};
